import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { LevelBadge, SUPPORTER_TIERS } from "@/components/LevelBadge";
import { LevelUpModal } from "@/components/LevelUpModal";
import { toast } from "sonner";
import { BookOpen, Calendar, Users, MessageSquare, Sparkles, ArrowUpRight, Settings, HandHeart, Camera, Send, Heart } from "lucide-react";

const TILES = [
  { to: "/devotionals", icon: BookOpen, t: "Devotionals", d: "Daily bread for the soul", bg: "bg-[#F5EFE2]" },
  { to: "/events", icon: Calendar, t: "Events", d: "Gather with the brothers", bg: "bg-[#E6ECF7]" },
  { to: "/directory", icon: Users, t: "Members", d: "Know your community", bg: "bg-[#E8EEE6]" },
  { to: "/messages", icon: Send, t: "Messages", d: "Message the brothers", bg: "bg-[#EAE8F5]" },
  { to: "/forum", icon: MessageSquare, t: "Community", d: "Speak and be heard", bg: "bg-[#F3E7DF]" },
  { to: "/prayers", icon: HandHeart, t: "Prayer Wall", d: "Bear one another's burdens", bg: "bg-[#F4E9EC]" },
  { to: "/assistant", icon: Sparkles, t: "Barnabas AI", d: "Christ-centered encouragement", bg: "bg-[#E4EFEC]" },
];

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [devo, setDevo] = useState(null);
  const [profile, setProfile] = useState({ bio: "", location: "", focus: "", daily_email: true });
  const [saving, setSaving] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [joining, setJoining] = useState(false);
  const [leveledUp, setLeveledUp] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/devotionals/daily").then((r) => setDevo(r.data)).catch(() => {});
    api.get("/supporter/status").then((r) => { setStatus(r.data); if (r.data.leveled_up) setLeveledUp(r.data.leveled_up); }).catch(() => {});
    if (user) setProfile({ bio: user.bio || "", location: user.location || "", focus: user.focus || "", daily_email: user.daily_email !== false });
  }, [user]);

  const becomeSupporter = async () => {
    setJoining(true);
    try {
      const { data } = await api.post("/supporter/join");
      setUser(data);
      toast.success("Welcome, Supporter! Your portal is unlocked.");
      navigate("/supporter");
    } catch { toast.error("Could not join right now — please try again."); }
    finally { setJoining(false); }
  };

  const isSupporter = status?.is_supporter || user?.role === "supporter" || user?.is_supporter;

  const saveProfile = async () => {
    setSaving(true);
    try { const { data } = await api.put("/profile", profile); setUser(data); } finally { setSaving(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch { /* toast handled globally */ } finally { setAvatarBusy(false); }
  };

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-14 pb-24">
      <LevelUpModal levelKey={leveledUp} onClose={() => setLeveledUp(null)} />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tight">
            Grace and peace, {user?.name?.split(" ")[0]}.
          </h1>
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="dashboard-admin-link" className="inline-flex items-center gap-2 mt-6 font-mono text-xs uppercase tracking-widest text-rust hover:gap-3 transition-all">
              <Settings size={14} /> Admin Console
            </Link>
          )}
        </Reveal>

        {/* Supporter status / Become a Supporter */}
        <Reveal delay={0.08}>
          {isSupporter ? (
            <div className="mt-10 border border-charcoal/10 bg-sand/40 p-6 md:p-8 flex flex-wrap items-center justify-between gap-6" data-testid="supporter-status-banner">
              <div className="flex items-center gap-5">
                {status && <LevelBadge level={status.level_key} size="lg" data-testid="dashboard-level-badge" />}
                <div>
                  <p className="font-serif text-2xl tracking-tight">You're a {status?.level || "Basic"} Supporter</p>
                  <p className="font-sans text-sm text-charcoal/60 mt-0.5">
                    {status?.next_level
                      ? `Give $${Number(status.to_next).toLocaleString()} more to reach ${status.next_level}.`
                      : "You've reached our highest tier. Thank you for your partnership."}
                  </p>
                </div>
              </div>
              <Link to="/supporter" data-testid="go-supporter-portal"
                className="group inline-flex items-center gap-3 bg-charcoal text-bone px-8 py-3.5 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors">
                Supporter Portal <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
            </div>
          ) : (
            <div className="mt-10 border border-charcoal/10 overflow-hidden" data-testid="become-supporter-card">
              <div className="grid grid-cols-1 md:grid-cols-12">
                <div className="md:col-span-7 bg-navy text-bone grain p-8 md:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3">
                    <Heart size={20} className="text-gold" strokeWidth={1.6} />
                    <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Partnership</span>
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-5 heading-light">Become a Supporter</h2>
                  <p className="font-sans text-bone/70 mt-3 max-w-md">
                    Membership is free — join in a single tap and unlock the Supporter Portal while keeping everything you have today. Rise through the tiers as you give.
                  </p>
                  <button onClick={becomeSupporter} disabled={joining} data-testid="become-supporter-btn"
                    className="group inline-flex items-center gap-3 mt-8 self-start bg-gold text-navy px-9 py-4 font-mono text-xs uppercase tracking-widest hover:bg-bone transition-colors disabled:opacity-50">
                    {joining ? "Joining..." : "Join Free"} <ArrowUpRight size={16} className="hover-arrow" />
                  </button>
                </div>
                <div className="md:col-span-5 p-8 md:p-10 bg-bone">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-charcoal/50">Supporter Levels</span>
                  <div className="mt-5 space-y-3">
                    {SUPPORTER_TIERS.map((t) => (
                      <div key={t.key} className="flex items-center justify-between gap-3" data-testid={`tier-row-${t.key}`}>
                        <div className="flex items-center gap-3">
                          <LevelBadge level={t.key} size="sm" />
                          <span className="font-sans text-xs text-charcoal/50">{t.blurb}</span>
                        </div>
                        <span className="font-serif text-lg text-charcoal/80">{t.min === 0 ? "Free" : `$${t.min.toLocaleString()}+`}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-16">
          <div className="md:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-charcoal/10 border border-charcoal/10">
              {TILES.map((t, i) => (
                <Reveal key={t.to} delay={i * 0.06} className={`${t.bg} border border-black`}>
                  <Link to={t.to} data-testid={`tile-${t.t.toLowerCase().replace(/\s/g, "-")}`}
                    className="group flex flex-col justify-between h-48 p-8 hover:bg-black/[0.04] transition-colors duration-400">
                    <t.icon size={26} strokeWidth={1.3} className="text-olive" />
                    <div>
                      <div className="flex items-center justify-between">
                        <h3 className="font-serif text-2xl tracking-tight">{t.t}</h3>
                        <ArrowUpRight size={18} className="hover-arrow" />
                      </div>
                      <p className="font-sans text-sm text-charcoal/60 mt-1">{t.d}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
              {devo && (
                <Reveal delay={0.3} className="bg-charcoal text-bone border border-black">
                  <Link to="/devotionals" className="group flex flex-col justify-between h-48 p-8">
                    <span className="font-mono text-xs uppercase tracking-widest text-gold">Today · {devo.scripture}</span>
                    <div>
                      <h3 className="heading-light font-serif text-2xl tracking-tight">{devo.title}</h3>
                      <p className="font-sans text-sm text-bone/50 mt-1 line-clamp-1">{devo.body}</p>
                    </div>
                  </Link>
                </Reveal>
              )}
            </div>
          </div>

          <div className="md:col-span-4">
            <Reveal delay={0.2}>
              <div className="border border-charcoal/10 p-8">
                <div className="flex items-center gap-4">
                  <button type="button" onClick={() => fileRef.current?.click()} data-testid="avatar-upload-btn"
                    className="relative group/av w-14 h-14 shrink-0 overflow-hidden">
                    {user?.avatar ? (
                      <img src={user.avatar} alt="" className="w-14 h-14 object-cover" />
                    ) : (
                      <div className="w-14 h-14 bg-navy text-bone flex items-center justify-center font-serif text-2xl">
                        {user?.name?.[0]}
                      </div>
                    )}
                    <span className="absolute inset-0 bg-charcoal/60 opacity-0 group-hover/av:opacity-100 transition-opacity flex items-center justify-center">
                      <Camera size={16} className="text-bone" strokeWidth={1.5} />
                    </span>
                    {avatarBusy && <span className="absolute inset-0 bg-charcoal/70 flex items-center justify-center"><span className="w-4 h-4 border-2 border-bone border-t-transparent rounded-full animate-spin" /></span>}
                  </button>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadAvatar} data-testid="avatar-input" />
                  <div>
                    <h3 className="font-serif text-xl leading-tight">{user?.name}</h3>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50">{user?.role}</span>
                  </div>
                </div>
                <div className="mt-8 space-y-5">
                  <ProfileField label="Focus" value={profile.focus} onChange={(v) => setProfile({ ...profile, focus: v })} testid="profile-focus" />
                  <ProfileField label="Location" value={profile.location} onChange={(v) => setProfile({ ...profile, location: v })} testid="profile-location" />
                  <div>
                    <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Bio</label>
                    <textarea value={profile.bio} rows={3} onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                      data-testid="profile-bio"
                      className="w-full bg-transparent border-b border-charcoal/30 py-2 font-sans text-sm focus:outline-none focus:border-olive transition-colors resize-none" />
                  </div>
                  <label className="flex items-center justify-between gap-3 cursor-pointer py-1" data-testid="daily-email-toggle">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Daily devotional email</span>
                    <input type="checkbox" checked={profile.daily_email} onChange={(e) => setProfile({ ...profile, daily_email: e.target.checked })} className="accent-navy w-4 h-4" />
                  </label>
                  <button onClick={saveProfile} disabled={saving} data-testid="profile-save"
                    className="w-full bg-charcoal text-bone py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
                    {saving ? "Saving..." : "Save Profile"}
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-transparent border-b border-charcoal/30 py-2 font-sans text-sm focus:outline-none focus:border-olive transition-colors" />
    </div>
  );
}
